import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useEscapeKey } from '../../../../hooks/useEscapeKey';
import DataDefinitionExplorer from './DataDefinitionExplorer';
import SamTypeManagerDialog from './SamTypeManagerDialog';
import NewDataDefinitionDialog from './NewDataDefinitionDialog';
import { DataDefinition, DataDefinitionType, fetchSamTypes } from '../../../../store/dataDefinitionsSlice';
import { useTranslation } from 'react-i18next';
import type { RootState, AppDispatch } from '../../../../store';
import { addNode } from '../../../../store/workflow/workflowSlice';
import { getId, getDefaultNodeData } from '../../canvas/WorkflowCanvas';
import { nodeFromDataDefinition } from '../../utils/dataDefinitionNode';
import { findFreePosition, nodeSize } from '../../utils/nodeLayout';
import { showAlert } from '../../../common/AppDialog';
import { useDefinitionPropagation } from './useDefinitionPropagation';

interface Props {
  onClose: () => void;
}

/**
 * 상단 아이콘으로 여는 "데이터 정의" 관리 화면.
 * 폴더(그룹) 생성, SAM파일/DB테이블 정의 생성, 레이아웃 갱신, 이동/복사, **워크플로에 추가**를 한다.
 *
 * 워크플로에 넣는 일은 예전에 오른쪽 '데이터 목록' 탭이 따로 했다. 같은 목록이 두 곳에 있어
 * 어디서 무엇을 하는지 헷갈렸다(현장 2026-09-22) — 찾고 고르는 자리를 여기 하나로 모았다.
 */
const DataDefinitionModal: React.FC<Props> = ({ onClose }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const currentWorkflow = useSelector((s: RootState) => s.workflow.current);
  const nodes = useSelector((s: RootState) => s.workflow.nodes);
  useEscapeKey(onClose);
  // 줄마다 SAM유형 이름을 보여 주려면 유형 목록이 있어야 한다 — 앱 시작 때는 받지 않으므로 여기서
  useEffect(() => { dispatch(fetchSamTypes()); }, [dispatch]);

  /** 고른 데이터를 지금 워크플로에 SAM파일/DB테이블 노드로 넣는다 — 다른 노드와 겹치지 않는 자리에 */
  const addToWorkflow = (def: DataDefinition): boolean => {
    if (!currentWorkflow) {
      showAlert(t('datadef:list.selectWorkflow'));
      return false;
    }
    const type = def.type === 'sam' ? 'csvData' : 'rdbData';
    const n = nodeFromDataDefinition(def, getDefaultNodeData(type).config);
    const topLevel = nodes.filter((x: any) => !x.parentId);
    const position = findFreePosition(topLevel as any, { x: 120, y: 120 }, nodeSize({ type } as any));
    dispatch(addNode({
      id: getId(), type: n.type, position,
      data: { label: n.label, config: n.config },
      draggable: true,
    } as any));
    return true;
  };
  const [createRequest, setCreateRequest] = useState<{ type: DataDefinitionType; folderPath: string } | null>(null);
  const [editing, setEditing] = useState<DataDefinition | null>(null);
  const propagate = useDefinitionPropagation();
  const [samTypesOpen, setSamTypesOpen] = useState(false);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
      <div className="bg-white rounded shadow-lg w-[780px] max-w-[92vw] h-[75vh] flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <h2 className="text-base font-semibold text-gray-900">{t('datadef:modal.title')}</h2>
          <div className="flex items-center gap-2">
            <button className="px-2 py-1 text-xs rounded border border-gray-300 bg-white hover:bg-gray-100 text-gray-700"
              onClick={() => setSamTypesOpen(true)}
              title={t('datadef:modal.samTypesTitle')}>
              {t('datadef:sam.title')}
            </button>
            <button className="text-gray-400 hover:text-gray-600" onClick={onClose}>✕</button>
          </div>
        </div>
        <div className="flex-1 overflow-hidden p-3">
          <DataDefinitionExplorer
            onRequestCreate={(type, folderPath) => setCreateRequest({ type, folderPath })}
            onAddToWorkflow={addToWorkflow}
            onRequestEdit={setEditing}
          />
        </div>
      </div>

      {createRequest && (
        <NewDataDefinitionDialog
          type={createRequest.type}
          folderPath={createRequest.folderPath}
          onClose={() => setCreateRequest(null)}
        />
      )}
      {editing && (
        <NewDataDefinitionDialog
          type={editing.type}
          folderPath={editing.path}
          editing={editing}
          onClose={() => setEditing(null)}
          // 고친 정의는 그 정의를 쓰는 워크플로 전부의 노드에 퍼뜨린다(정의 ↔ 노드 연결)
          onSaved={(item) => { propagate([item.id]); }}
        />
      )}
      {samTypesOpen && <SamTypeManagerDialog onClose={() => setSamTypesOpen(false)} />}
    </div>
  );
};

export default DataDefinitionModal;
